// Typed access to the app-local CustomerDisplay Android plugin (secondary
// screen presentation). Ported from cartadigital-pos app/utils/customer-display.ts.
import { Capacitor, registerPlugin } from '@capacitor/core';
import type { ICustomerDisplayPayload } from '@/domain/customer-display/customer-display.types';

export interface ICustomerDisplayShowResult {
  available: boolean;
  displayId?: number;
}

export interface ICustomerDisplaySetStateResult {
  available: boolean;
  applied: boolean;
}

export interface ICustomerDisplayCapabilities {
  nativeVersion: string;
  maxProtocolVersion: number;
}

export interface ICustomerDisplayPlugin {
  show: (options?: {
    /**
     * Optional mini-app URL override. When omitted the native side loads
     * `<server.url>/customer-display/index.html`.
     */
    url?: string;
  }) => Promise<ICustomerDisplayShowResult>;
  hide: () => Promise<void>;
  setState: (options: {
    payload?: ICustomerDisplayPayload;
    /**
     * Optional alternative for payload transport. Prefer `payload` in normal use.
     */
    payloadJson?: string;
  }) => Promise<ICustomerDisplaySetStateResult>;
  /**
   * Feature detection for the rarely-updated binary (the APK ships seldom
   * while the web app deploys continuously).
   */
  getCapabilities: () => Promise<ICustomerDisplayCapabilities>;
}

export const CustomerDisplay =
  registerPlugin<ICustomerDisplayPlugin>('CustomerDisplay');

/**
 * The customer display exists ONLY on the Capacitor Android build — this
 * alone guarantees the desktop browser (and iOS) never activates it. The
 * plugin check additionally excludes legacy shells (old APKs loading this
 * bundle remotely without the plugin), where every call would fail and the
 * retry loop would spin forever.
 */
export function isCustomerDisplayAvailable(): boolean {
  const isNative = Capacitor.isNativePlatform?.() ?? false;
  return (
    isNative &&
    Capacitor.getPlatform() === 'android' &&
    Capacitor.isPluginAvailable('CustomerDisplay')
  );
}
